/**
 * Whose cs-analysis session this browser holds, decided once per page load
 * before the first cs-analysis call (CT-15747; cs-analysis
 * docs/design-chat-and-access.md "Staff sign-out").
 *
 * Why: the `cs-analysis-session` cookie is stateless and lives up to 7 days;
 * a sign-out that is not the CSP's (the design app, the login page), or a CSP
 * sign-out before cs-analysis had its sign-out route, leaves it in the
 * browser. The api takes that cookie before anything else, and the CSP signed
 * in again only on a 401, so the next person to sign in to Infurnia on the
 * browser went on with the previous person's session: their roles, their
 * scope, sending as them.
 *
 * So at page load the CSP compares the api's session (GET /api/v1/auth/me) with
 * the person infurnia-main says is signed in (`/user/get_details`).
 *
 * `me`: `{ status, user }` from auth/me (`user` = its `{ user }`, null when
 * the body was not read), or null when the call did not answer.
 * `details`: get_details' `user_details`, or null.
 *
 * An id missing on either side (the person's or the org's: undefined, null, or
 * text blank once trimmed) is never a match: the session is replaced before
 * anything is compared (Copilot on #273: String(undefined) is 'undefined' on
 * both sides, so two missing org ids read as the same org).
 */
export const SESSION_KEEP = 'keep';
export const SESSION_REPLACE = 'replace';
export const SESSION_NONE = 'none';
export const SESSION_UNKNOWN = 'unknown';

/** An id that names nothing: undefined, null, or text blank once trimmed. */
function missing_id(value) {
    return value === undefined || value === null || String(value).trim() === '';
}

export function session_verdict(me, details) {
    if (!me) return SESSION_UNKNOWN;
    // No session (none, expired, or signed elsewhere): the first call's 401 signs in, as before.
    if (me.status === 401) return SESSION_NONE;
    // Any other refusal or failure says nothing about whose session it is.
    if (me.status !== 200) return SESSION_UNKNOWN;
    // A session, and infurnia-main names nobody (not signed in, or get_details failed): not known to be the person's
    // at this browser, so replaced too (a sign-in then either makes the person's own, or fails and nothing is sent).
    if (!details || missing_id(details.id)) return SESSION_REPLACE;
    const user = me.user;
    // A session the CSP cannot read is not known to be this person's: replaced.
    if (!user || missing_id(user.id)) return SESSION_REPLACE;
    // The org missing on either side: the session's org (or the page's) was never established, so it is not known to be
    // this org's session: replaced (never compared: two missing ids would read as the same text).
    if (missing_id(user.storeId) || missing_id(details.store_id)) return SESSION_REPLACE;
    // Another person, or the same person's session for another org: replaced before any other call.
    if (String(user.id) !== String(details.id)) return SESSION_REPLACE;
    if (String(user.storeId) !== String(details.store_id)) return SESSION_REPLACE;
    return SESSION_KEEP;
}
